import { truncateLabel } from "@/utils";
import React, { useState, useCallback, ReactNode } from "react";
import {
    BreadcrumbContext,
    BreadcrumbContextType,
    BreadcrumbItem,
} from "./BreadcrumbContext";

interface BreadcrumbProviderProps {
    /**
     * Child components that will have access to breadcrumb context
     */
    children: ReactNode;
    /**
     * Optional initial breadcrumbs to set when provider mounts
     */
    initialBreadcrumbs?: BreadcrumbItem[];
}

/**
 * Provider component for breadcrumb navigation state management
 *
 * This provider manages the breadcrumb navigation state and provides methods
 * to update breadcrumbs from anywhere in the component tree.
 *
 * @example
 * ```tsx
 * <BreadcrumbProvider>
 *   <App />
 * </BreadcrumbProvider>
 * ```
 */
export const BreadcrumbProvider: React.FC<BreadcrumbProviderProps> = ({
    children,
    initialBreadcrumbs = [],
}) => {
    const truncatedInitialBreadcrumbs = initialBreadcrumbs.map((item) => ({
        ...item,
        label: truncateLabel(item.label),
    }));
    const [breadcrumbs, setBreadcrumbsState] = useState<BreadcrumbItem[]>(
        truncatedInitialBreadcrumbs
    );

    /**
     * Replace the entire breadcrumb array
     */
    const setBreadcrumbs = useCallback((newBreadcrumbs: BreadcrumbItem[]) => {
        const truncatedBreadcrumbs = newBreadcrumbs.map((item) => ({
            ...item,
            label: truncateLabel(item.label),
        }));
        setBreadcrumbsState(truncatedBreadcrumbs);
    }, []);

    /**
     * Add a single breadcrumb to the end of the current array
     */
    const addBreadcrumb = useCallback((item: BreadcrumbItem) => {
        const truncatedItem = { ...item, label: truncateLabel(item.label) };
        setBreadcrumbsState((prev) => [...prev, truncatedItem]);
    }, []);

    /**
     * Remove all breadcrumbs
     */
    const clearBreadcrumbs = useCallback(() => {
        setBreadcrumbsState([]);
    }, []);

    /**
     * Update a specific breadcrumb by index
     */
    const updateBreadcrumb = useCallback(
        (index: number, item: Partial<BreadcrumbItem>) => {
            setBreadcrumbsState((prev) => {
                if (index < 0 || index >= prev.length) {
                    console.warn(
                        `BreadcrumbProvider: Invalid index ${index} for updateBreadcrumb`
                    );
                    return prev;
                }

                const newBreadcrumbs = [...prev];
                const updatedItem = { ...newBreadcrumbs[index], ...item };
                if (updatedItem.label) {
                    updatedItem.label = truncateLabel(updatedItem.label);
                }
                newBreadcrumbs[index] = updatedItem;
                return newBreadcrumbs;
            });
        },
        []
    );

    /**
     * Remove a specific breadcrumb by index
     */
    const removeBreadcrumb = useCallback((index: number) => {
        setBreadcrumbsState((prev) => {
            if (index < 0 || index >= prev.length) {
                console.warn(
                    `BreadcrumbProvider: Invalid index ${index} for removeBreadcrumb`
                );
                return prev;
            }

            return prev.filter((_, i) => i !== index);
        });
    }, []);

    const contextValue: BreadcrumbContextType = {
        breadcrumbs,
        setBreadcrumbs,
        addBreadcrumb,
        clearBreadcrumbs,
        updateBreadcrumb,
        removeBreadcrumb,
    };

    return (
        <BreadcrumbContext.Provider value={contextValue}>
            {children}
        </BreadcrumbContext.Provider>
    );
};
